import { format } from "date-fns";

/**
 * Regras e textos da seção de cancelamento do pedido. Contrato da API em
 * .claude/docs/frontend_order_cancellation.md; layout em design_handoff_cancelamento_pedido/README.md.
 */

export const CANCELLATION_REASONS = [
  "Erro de quantidade ou série",
  "Desistência da compra",
  "Vou refazer o pedido",
  "Outro motivo",
] as const;

/** Fora da lista, mas ainda gravados em pedidos antigos — seguem reconhecidos na exibição. */
const LEGACY_CANCELLATION_REASONS = [
  "Itens duplicados no pedido",
  "Erro de quantidade ou SKU",
  "A escola desistiu da compra",
] as const;

export const CANCELLATION_DETAILS_MAX_LENGTH = 250;

/**
 * - `direct`: pedido ainda não saiu pro ERP → a API cancela na hora.
 * - `ticket`: pedido já integrado → a API abre (ou reaproveita) um chamado no Zendesk.
 */
export type CancellationMode = "direct" | "ticket";

export const CANCELLATION_MODE_COPY: Record<
  CancellationMode,
  { description: string; notice: string; submitLabel: string; pendingLabel: string }
> = {
  direct: {
    description: "",
    notice: "Ao confirmar o cancelamento, a ação não poderá ser desfeita.",
    submitLabel: "Confirmar cancelamento",
    pendingLabel: "Cancelando…",
  },
  ticket: {
    description: "",
    notice: "Ao confirmar, sua solicitação será registrada para atendimento.",
    submitLabel: "Solicitar cancelamento",
    pendingLabel: "Solicitando…",
  },
};

export interface CancellationReason {
  reason: string;
  details?: string;
}

// A API só tem o campo livre `reason`: motivo, detalhes e status do pedido viajam juntos (o backend
// repassa o texto pro chamado no Zendesk) e são separados na exibição.
const REASON_DETAILS_SEPARATOR = " — ";
const ORDER_STATUS_SEPARATOR = " | Status do pedido: ";

interface CancellationReasonInput extends CancellationReason {
  /** Status exibido na tela no momento da solicitação — vai pro chamado, não aparece no acompanhamento. */
  orderStatus?: string;
}

export function buildCancellationReason({ reason, details, orderStatus }: CancellationReasonInput): string {
  const trimmedDetails = details?.trim();
  const reasonWithDetails = trimmedDetails ? `${reason}${REASON_DETAILS_SEPARATOR}${trimmedDetails}` : reason;
  return orderStatus ? `${reasonWithDetails}${ORDER_STATUS_SEPARATOR}${orderStatus}` : reasonWithDetails;
}

/** Motivos antigos (texto livre, sem motivo da lista) voltam inteiros em `reason`. */
export function parseCancellationReason(raw?: string | null): CancellationReason | null {
  const statusIndex = raw?.lastIndexOf(ORDER_STATUS_SEPARATOR) ?? -1;
  const value = (statusIndex >= 0 ? raw?.slice(0, statusIndex) : raw)?.trim();
  if (!value) return null;

  const knownReason = [...CANCELLATION_REASONS, ...LEGACY_CANCELLATION_REASONS].find(
    (reason) => value === reason || value.startsWith(`${reason}${REASON_DETAILS_SEPARATOR}`)
  );
  if (!knownReason) return { reason: value };

  const details = value.slice(knownReason.length + REASON_DETAILS_SEPARATOR.length).trim();
  return { reason: knownReason, details: details || undefined };
}

// `status` vem cru do Zendesk (seção 3.2 do contrato).
const TICKET_STATUS_LABELS: Record<string, string> = {
  new: "novo",
  open: "aberto",
  pending: "pendente",
  hold: "em espera",
  solved: "resolvido",
  closed: "fechado",
};

export function formatTicketStatus(status?: string | null): string | null {
  if (!status) return null;
  return TICKET_STATUS_LABELS[status.toLowerCase()] ?? status;
}

export function isTicketResolved(status?: string | null): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "solved" || normalized === "closed";
}

/** Data curta usada no stepper — ex.: "22/09, 09:28". */
export function formatStepDate(date?: string | null): string | null {
  return date ? format(new Date(date), "dd/MM, HH:mm") : null;
}
